// src/components/PdfViewer.js

import React, { useState, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { 
  FaSearchPlus, 
  FaSearchMinus, 
  FaRedo, 
  FaUndo, 
  FaChevronLeft, 
  FaChevronRight, 
  FaBookOpen, 
  FaAlignJustify,
  FaExpand
} from 'react-icons/fa';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import './PdfViewer.css';

// Point to the worker file copied to the public folder
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.mjs';

export const PdfViewer = ({ fileUrl, allowDownload = false }) => {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [rotate, setRotate] = useState(0);
  const [viewMode, setViewMode] = useState('scroll'); // scroll | single
  
  const containerRef = useRef(null);

  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setPageNumber(1);
  }

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  };

  const handleRotateClockwise = () => {
    setRotate(prev => (prev + 90) % 360);
  };

  const handleRotateCounterClockwise = () => {
    setRotate(prev => (prev + 270) % 360);
  };

  const handleFullscreen = () => {
    if (containerRef.current) {
      if (!document.fullscreenElement) {
        containerRef.current.requestFullscreen().catch(err => {
          console.error(`Error attempting to enable full-screen mode: ${err.message}`);
        });
      } else {
        document.exitFullscreen();
      }
    }
  };

  return (
    <div className="pdf-viewer-container" ref={containerRef}>
      {/* Dynamic PDF Toolbar */}
      <div className="pdf-toolbar">
        <div className="pdf-toolbar-group">
          {!allowDownload && <span className="pdf-view-only-badge">View Only</span>}
          {viewMode === 'single' && numPages && (
            <div className="pdf-page-indicator">
              <button 
                className="pdf-toolbar-btn" 
                onClick={() => setPageNumber(p => Math.max(1, p - 1))} 
                disabled={pageNumber <= 1}
                title="Previous Page"
              >
                <FaChevronLeft />
              </button>
              <span>{pageNumber} of {numPages}</span>
              <button 
                className="pdf-toolbar-btn" 
                onClick={() => setPageNumber(p => Math.min(numPages, p + 1))} 
                disabled={pageNumber >= numPages}
                title="Next Page"
              >
                <FaChevronRight />
              </button>
            </div>
          )}
        </div>

        <div className="pdf-toolbar-group">
          {/* Zoom controls */}
          <button 
            className="pdf-toolbar-btn" 
            onClick={handleZoomOut} 
            disabled={scale <= 0.5}
            title="Zoom Out"
          >
            <FaSearchMinus />
          </button>
          <span className="pdf-zoom-text">{Math.round(scale * 100)}%</span>
          <button 
            className="pdf-toolbar-btn" 
            onClick={handleZoomIn} 
            disabled={scale >= 3.0}
            title="Zoom In"
          >
            <FaSearchPlus />
          </button>
          
          <div className="pdf-toolbar-divider" />

          {/* Rotation controls */}
          <button 
            className="pdf-toolbar-btn" 
            onClick={handleRotateCounterClockwise} 
            title="Rotate Counter-Clockwise"
          >
            <FaUndo />
          </button>
          <button 
            className="pdf-toolbar-btn" 
            onClick={handleRotateClockwise} 
            title="Rotate Clockwise"
          >
            <FaRedo />
          </button>

          <div className="pdf-toolbar-divider" />

          {/* Layout Mode controls */}
          <button 
            className={`pdf-toolbar-btn ${viewMode === 'scroll' ? 'active' : ''}`} 
            onClick={() => setViewMode('scroll')} 
            title="Continuous Scroll Mode"
          >
            <FaAlignJustify /> Scroll
          </button>
          <button 
            className={`pdf-toolbar-btn ${viewMode === 'single' ? 'active' : ''}`} 
            onClick={() => setViewMode('single')} 
            title="Single Page Mode"
          >
            <FaBookOpen /> Page
          </button>

          <div className="pdf-toolbar-divider" />

          {/* Presentation control */}
          <button 
            className="pdf-toolbar-btn" 
            onClick={handleFullscreen} 
            title="Toggle Fullscreen Presentation"
          >
            <FaExpand />
          </button>
        </div>
      </div>

      {/* Main viewport */}
      <div className="pdf-scroll-viewport">
        <Document
          file={fileUrl}
          onLoadSuccess={onDocumentLoadSuccess}
          loading={
            <div className="pdf-loading-overlay">
              <div className="pdf-spinner" />
              <span>Decoding PDF structure...</span>
            </div>
          }
          error={
            <div className="pdf-loading-overlay">
              <span>Failed to load PDF document. Please try downloading it instead.</span>
            </div>
          }
        >
          {viewMode === 'scroll' && numPages ? (
            Array.from(new Array(numPages), (el, index) => (
              <div key={`page_${index + 1}`} className="pdf-page-card">
                <Page 
                  pageNumber={index + 1} 
                  scale={scale} 
                  rotate={rotate}
                  loading=""
                />
                <div className="pdf-page-number-floating">
                  Page {index + 1} of {numPages}
                </div>
              </div>
            ))
          ) : numPages ? (
            <div className="pdf-page-card">
              <Page 
                pageNumber={pageNumber} 
                scale={scale} 
                rotate={rotate}
                loading=""
              />
              <div className="pdf-page-number-floating">
                Page {pageNumber} of {numPages}
              </div>
            </div>
          ) : null}
        </Document>
      </div>
    </div>
  );
};